/**
 * T28 — Exportação HTML/PDF local.
 *
 * Privacidade: nunca emite CPF, CNPJ, razão social ou nome de arquivo.
 * Apenas dados anônimos/agregados presentes no ReportModelT27.
 */

import type { ReportModelT27, ReportSectionT27 } from './report.types';

// =========================================================================
// Helpers internos
// =========================================================================

/** Escapa caracteres HTML para evitar XSS em strings injetadas. */
function escHtml(s: unknown): string {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** CSS inline com tokens Pontotel — valores literais para standalone HTML. */
const INLINE_CSS = `
  /* Reset básico */
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  /* Tokens Pontotel — literais para HTML standalone */
  :root {
    --ptt-black:      #000000;
    --ptt-white:      #FFFFFF;
    --ptt-yellow-100: #FCCC31;
    --ptt-yellow-10:  #FEF3CF;
    --ptt-purple-100: #7F3D7D;
    --ptt-purple-300: #401144;
    --ptt-gray-10:    #F5F5F5;
    --ptt-gray-20:    #E7E7E7;
    --ptt-gray-100:   #7D7D7D;
    --ptt-gray-200:   #565656;
    --ptt-gray-300:   #323232;
    --ptt-green-100:  #2CE057;
    --ptt-orange-100: #FB7224;
  }

  body {
    font-family: Arial, Helvetica, sans-serif;
    font-size: 14px;
    color: #323232;
    background: #ffffff;
    line-height: 1.6;
    padding: 24px 32px;
    max-width: 900px;
    margin: 0 auto;
  }

  h1 { font-size: 1.75rem; color: #000000; margin-bottom: 0.25rem; }
  h2 { font-size: 1.25rem; color: #323232; margin-bottom: 0.5rem; margin-top: 1.5rem; border-bottom: 2px solid #E7E7E7; padding-bottom: 0.25rem; }
  h3 { font-size: 1rem; color: #565656; margin-bottom: 0.25rem; margin-top: 1rem; }
  p  { margin-bottom: 0.5rem; color: #565656; }

  /* Aviso piloto */
  .pilot-notice {
    background: #FEF3CF;
    border-left: 4px solid #FCCC31;
    padding: 10px 14px;
    margin-bottom: 20px;
    font-size: 0.875rem;
    color: #323232;
    border-radius: 4px;
  }

  /* Capa */
  .section-cover {
    background: #F5F5F5;
    border-radius: 6px;
    padding: 20px 24px;
    margin-bottom: 24px;
  }
  .section-cover .cover-meta { color: #7D7D7D; font-size: 0.875rem; margin-top: 8px; }
  .cover-score { font-size: 2rem; font-weight: 700; color: #7F3D7D; display: inline-block; margin-right: 8px; }
  .cover-band  { font-size: 1rem; color: #565656; }

  /* Highlights do resumo executivo */
  .highlights-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
    gap: 12px;
    margin-top: 12px;
  }
  .highlight-card {
    background: #F5F5F5;
    border-radius: 4px;
    padding: 12px;
    border-top: 3px solid #7F3D7D;
  }
  .highlight-card--cost     { border-top-color: #FB7224; }
  .highlight-card--rework   { border-top-color: #7F3D7D; }
  .highlight-card--risk     { border-top-color: #E64BC8; }
  .highlight-card--score    { border-top-color: #FCCC31; }
  .highlight-card--visibility { border-top-color: #2062E1; }
  .hl-label { font-size: 0.75rem; color: #7D7D7D; }
  .hl-value { font-size: 1.25rem; font-weight: 700; color: #323232; margin: 2px 0; }
  .hl-explanation { font-size: 0.75rem; color: #565656; }
  .hl-assumption  { font-size: 0.7rem; color: #7D7D7D; font-style: italic; margin-top: 2px; }

  /* Tabelas de métricas */
  table { width: 100%; border-collapse: collapse; margin-top: 10px; font-size: 0.875rem; }
  th { text-align: left; padding: 6px 8px; background: #F5F5F5; border-bottom: 2px solid #E7E7E7; color: #565656; font-weight: 600; }
  td { padding: 6px 8px; border-bottom: 1px solid #E7E7E7; color: #323232; }
  tr:last-child td { border-bottom: none; }

  /* Score */
  .score-total { font-size: 3rem; font-weight: 700; color: #7F3D7D; }
  .score-band  { font-size: 1rem; background: #FEF3CF; color: #323232; padding: 2px 10px; border-radius: 100px; display: inline-block; }

  /* Recomendações */
  .rec-item { margin-bottom: 12px; padding: 10px 12px; background: #F5F5F5; border-radius: 4px; }
  .rec-priority { font-size: 0.7rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; padding: 2px 6px; border-radius: 4px; color: #ffffff; display: inline-block; margin-bottom: 4px; }
  .rec-priority--critical { background: #323232; }
  .rec-priority--high     { background: #7F3D7D; }
  .rec-priority--medium   { background: #7D7D7D; }
  .rec-priority--low      { background: #E7E7E7; color: #323232; }
  .rec-title { font-weight: 600; color: #323232; }
  .rec-body  { font-size: 0.875rem; color: #565656; margin-top: 4px; }

  /* Issues de limitações */
  .issue-item { margin-bottom: 8px; padding: 8px 10px; border-radius: 4px; font-size: 0.875rem; }
  .issue-item--blocking { background: #FEF3CF; border-left: 4px solid #FB7224; }
  .issue-item--error    { background: #F5F5F5; border-left: 4px solid #7D7D7D; }
  .issue-item--warning  { background: #F5F5F5; border-left: 4px solid #E7E7E7; }

  /* Seção genérica */
  .report-section { margin-bottom: 28px; page-break-inside: avoid; }
  .section-intro  { color: #7D7D7D; font-size: 0.875rem; margin-bottom: 10px; }

  /* Print */
  @media print {
    body { padding: 0; font-size: 12px; }
    .report-section { page-break-inside: avoid; }
    .highlights-grid { grid-template-columns: repeat(3, 1fr); }
    h2 { page-break-after: avoid; }
    .pilot-notice { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    .highlight-card { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  }
`;

// =========================================================================
// Renderizadores por kind de seção
// =========================================================================

function renderCover(section: ReportSectionT27, report: ReportModelT27): string {
  const c = section.content as Record<string, unknown>;
  return `
    <div class="section-cover">
      <h1>${escHtml(section.title)}</h1>
      <div class="cover-meta">Período: ${escHtml(c['period'] ?? report.periodLabel)}</div>
      <div class="cover-meta">Colaboradores analisados: <strong>${escHtml(c['analyzedEmployees'])}</strong></div>
      <div style="margin-top:12px;">
        <span class="cover-score">${escHtml(c['scoreTotal'])}</span><span style="font-size:1rem;color:#7D7D7D;">/100</span>
        &nbsp;<span class="cover-band">${escHtml(c['scoreBand'])}</span>
      </div>
      ${c['limitationNote'] ? `<div class="cover-meta" style="margin-top:8px;">${escHtml(c['limitationNote'])}</div>` : ''}
      <div class="cover-meta" style="margin-top:8px;">Gerado em: ${escHtml(report.generatedAt)}</div>
    </div>`;
}

function renderExecutiveSummary(section: ReportSectionT27): string {
  const c = section.content as { highlights?: Array<Record<string, unknown>> };
  const highlights = c.highlights ?? [];
  const cards = highlights
    .map(
      (h) => `
      <div class="highlight-card highlight-card--${escHtml(h['category'])}">
        <div class="hl-label">${escHtml(h['label'])}</div>
        <div class="hl-value">${escHtml(h['formattedValue'])}</div>
        <div class="hl-explanation">${escHtml(h['explanation'])}</div>
        ${h['assumption'] ? `<div class="hl-assumption">${escHtml(h['assumption'])}</div>` : ''}
      </div>`,
    )
    .join('');
  return `<div class="highlights-grid">${cards}</div>`;
}

function renderBase(section: ReportSectionT27): string {
  const c = section.content as Record<string, unknown>;
  const rows = [
    ['Colaboradores analisados', c['analyzedEmployees']],
    ['Dias analisados', c['analyzedDays']],
    ['Total de marcações', c['totalPunches']],
    ['Jornadas completas', c['completeJourneys']],
    ['Jornadas pendentes', c['pendingJourneys']],
  ];
  return `
    <table>
      <thead><tr><th>Métrica</th><th>Valor</th></tr></thead>
      <tbody>
        ${rows.map(([k, v]) => `<tr><td>${escHtml(k)}</td><td>${escHtml(v)}</td></tr>`).join('')}
      </tbody>
    </table>`;
}

function renderMetrics(section: ReportSectionT27): string {
  const c = section.content as Record<string, Record<string, unknown>>;
  const cost = c['cost'] ?? {};
  const absence = c['absence'] ?? {};
  const rework = c['rework'] ?? {};
  const risk = c['risk'] ?? {};

  const costRows = [
    ['Minutos excedentes', cost['excessMinutes']],
    ['Min. excedentes / colaborador', cost['excessMinutesPerEmployee']],
  ];
  const absenceRows = [
    ['Jornadas mais curtas que a escala', absence['shortJourneys']],
    ['Minutos de jornada mais curta', absence['shortJourneyMinutes']],
    ['Faltas (estimado)', absence['faltas']],
  ];
  const reworkRows = [
    ['Marcações a adicionar (estimado)', rework['inferredManualPunches']],
    ['Marcações redundantes (estimado)', rework['inferredDisregardedPunches']],
    ['Taxa de ajuste', rework['adjustmentRate']],
  ];
  const riskRows = [
    ['Sinais de risco total', risk['totalRiskSignals']],
    ['Pares inconsistentes', risk['inconsistentPairsCount']],
    ['Jornadas excessivas', risk['excessiveJourneysCount']],
    ['Intervalos intrajornada curtos', risk['shortIntradayBreaksCount']],
    ['Interjornadas curtas', risk['shortInterjourneyBreaksCount']],
    ['Ponto britânico potencial', risk['potentialBritishPointCount']],
    ['Integridade documental', risk['documentIntegrityCount']],
  ];

  function tableOf(rows: unknown[][]): string {
    return `<table>
      <thead><tr><th>Métrica</th><th>Valor</th></tr></thead>
      <tbody>${rows.map(([k, v]) => `<tr><td>${escHtml(k)}</td><td>${escHtml(v)}</td></tr>`).join('')}</tbody>
    </table>`;
  }

  return `
    <h3>Custo</h3>${tableOf(costRows)}
    <h3>Ausências</h3>${tableOf(absenceRows)}
    <h3>Retrabalho</h3>${tableOf(reworkRows)}
    <h3>Risco</h3>${tableOf(riskRows)}`;
}

function renderScore(section: ReportSectionT27): string {
  const c = section.content as Record<string, unknown>;
  const dimensions = (c['dimensions'] as Array<Record<string, unknown>>) ?? [];
  const dimRows = dimensions
    .map(
      (d) =>
        `<tr>
           <td>${escHtml(d['label'])}</td>
           <td>${escHtml(d['value'])}</td>
           <td>${escHtml((Number(d['weight']) * 100).toFixed(0))}%</td>
           <td style="font-size:0.8rem;color:#7D7D7D;">${escHtml(d['rationale'])}</td>
         </tr>`,
    )
    .join('');
  return `
    <div style="margin-bottom:12px;">
      <span class="score-total">${escHtml(c['total'])}</span><span style="font-size:1rem;color:#7D7D7D;">/100</span>
      &nbsp;<span class="score-band">${escHtml(c['bandLabel'])}</span>
    </div>
    <table>
      <thead><tr><th>Dimensão</th><th>Pontos</th><th>Peso</th><th>Racional</th></tr></thead>
      <tbody>${dimRows}</tbody>
    </table>`;
}

function renderRecommendations(section: ReportSectionT27): string {
  const c = section.content as { recommendations?: Array<Record<string, unknown>> };
  const recs = c.recommendations ?? [];
  return recs
    .map(
      (r) => `
      <div class="rec-item">
        <span class="rec-priority rec-priority--${escHtml(r['priority'])}">${escHtml(r['priority'])}</span>
        <div class="rec-title">${escHtml(r['title'])}</div>
        ${r['body'] ? `<div class="rec-body">${escHtml(r['body'])}</div>` : ''}
        ${r['nextStep'] ? `<div class="rec-body" style="margin-top:4px;"><strong>Próximo passo:</strong> ${escHtml(r['nextStep'])}</div>` : ''}
      </div>`,
    )
    .join('');
}

function renderNextSteps(section: ReportSectionT27): string {
  const c = section.content as { steps?: Array<Record<string, unknown>> };
  const steps = c.steps ?? [];
  return steps
    .map(
      (s, i) => `
      <div class="rec-item">
        <span class="rec-priority rec-priority--${escHtml(s['priority'])}">${i + 1}</span>
        <div class="rec-title" style="font-size:0.875rem;">${escHtml(s['nextStep'])}</div>
      </div>`,
    )
    .join('');
}

function renderLimitations(section: ReportSectionT27): string {
  const c = section.content as Record<string, unknown>;
  const issues = (c['issues'] as Array<Record<string, unknown>>) ?? [];
  const summary = `
    <table style="margin-bottom:12px;">
      <thead><tr><th>Indicador</th><th>Valor</th></tr></thead>
      <tbody>
        <tr><td>Confiabilidade</td><td>${escHtml(c['confidence'])}</td></tr>
        <tr><td>Issues bloqueantes</td><td>${escHtml(c['blockingCount'])}</td></tr>
        <tr><td>Erros</td><td>${escHtml(c['errorCount'])}</td></tr>
        <tr><td>Avisos</td><td>${escHtml(c['warningCount'])}</td></tr>
      </tbody>
    </table>`;
  const issuesList = issues
    .map((iss) => {
      const sev = String(iss['severity'] ?? 'warning');
      const cls = sev === 'blocking' ? 'blocking' : sev === 'error' ? 'error' : 'warning';
      return `<div class="issue-item issue-item--${cls}">
        <strong>[${escHtml(iss['severity'])}] ${escHtml(iss['code'])}</strong>: ${escHtml(iss['message'])}
        ${iss['recommendation'] ? `<div style="margin-top:2px;font-size:0.8rem;">${escHtml(iss['recommendation'])}</div>` : ''}
      </div>`;
    })
    .join('');
  return summary + issuesList;
}

function renderAssumptions(section: ReportSectionT27): string {
  const c = section.content as { items?: Array<{ label: string; value: string }> };
  const items = c.items ?? [];
  return `
    <table>
      <thead><tr><th>Premissa</th><th>Valor</th></tr></thead>
      <tbody>
        ${items.map((it) => `<tr><td>${escHtml(it.label)}</td><td>${escHtml(it.value)}</td></tr>`).join('')}
      </tbody>
    </table>`;
}

/** Renderiza uma seção conforme seu kind. Retorna HTML. */
function renderSection(section: ReportSectionT27, report: ReportModelT27): string {
  let body: string;
  switch (section.kind) {
    case 'cover':
      return renderCover(section, report);
    case 'executive-summary':
      body = renderExecutiveSummary(section);
      break;
    case 'base':
      body = renderBase(section);
      break;
    case 'metrics':
      body = renderMetrics(section);
      break;
    case 'score':
      body = renderScore(section);
      break;
    case 'recommendations':
      body = renderRecommendations(section);
      break;
    case 'next-steps':
      body = renderNextSteps(section);
      break;
    case 'limitations':
      body = renderLimitations(section);
      break;
    case 'assumptions':
      body = renderAssumptions(section);
      break;
    default:
      body = '';
  }

  return `
    <div class="report-section" data-section-kind="${escHtml(section.kind)}">
      <h2>${escHtml(section.title)}</h2>
      ${section.intro ? `<p class="section-intro">${escHtml(section.intro)}</p>` : ''}
      ${body}
    </div>`;
}

// =========================================================================
// API pública
// =========================================================================

/**
 * Constrói um Blob HTML completo (com inline CSS) do relatório.
 * NUNCA inclui CPF, CNPJ, razão social ou nome de arquivo.
 */
export function exportReportAsHtml(report: ReportModelT27): Blob {
  const sectionsHtml = report.sections.map((s) => renderSection(s, report)).join('\n');

  const html = `<!DOCTYPE html>
<html lang="pt-br">
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <title>Diagnóstico de Jornada — ${escHtml(report.periodLabel)}</title>
  <style>${INLINE_CSS}</style>
</head>
<body>
  <div class="pilot-notice">
    <strong>Aviso piloto:</strong> ${escHtml(report.pilotNotice)}
  </div>
  ${sectionsHtml}
  <footer style="margin-top:40px;padding-top:12px;border-top:1px solid #E7E7E7;font-size:0.75rem;color:#7D7D7D;">
    Diagnóstico de Jornada — Versão ${escHtml(report.version)} — Gerado em ${escHtml(report.generatedAt)}
  </footer>
</body>
</html>`;

  return new Blob([html], { type: 'text/html' });
}

/**
 * Triggera download de um Blob via createObjectURL + link click.
 */
export function downloadBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Chama window.print(). Acionar PrintableReport antes via key prop.
 */
export function printReportToPdf(): void {
  window.print();
}

/**
 * Nome de arquivo seguro: data + período + score. SEM CNPJ/razão social.
 * Formato: diagnostico-jornada-YYYY-MM-DD-score-XX.html
 * Ex: diagnostico-jornada-2026-04-25-score-67.html
 */
export function buildSafeReportFileName(report: ReportModelT27): string {
  // Extrai data de geração: "YYYY-MM-DDTHH:mm" → "YYYY-MM-DD"
  const datePart = report.generatedAt.split('T')[0] ?? new Date().toISOString().split('T')[0];

  // Busca scoreTotal da seção cover ou score
  let scoreTotal = 0;
  for (const section of report.sections) {
    if (section.kind === 'cover') {
      const c = section.content as Record<string, unknown>;
      if (typeof c['scoreTotal'] === 'number') {
        scoreTotal = c['scoreTotal'];
        break;
      }
    }
    if (section.kind === 'score') {
      const c = section.content as Record<string, unknown>;
      if (typeof c['total'] === 'number') {
        scoreTotal = c['total'];
        break;
      }
    }
  }

  return `diagnostico-jornada-${datePart}-score-${scoreTotal}.html`;
}
